import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiService from '@/services/api';

export interface SubscriptionData {
  id: string;
  user_id: string;
  plan_id: string;
  status: 'active' | 'inactive' | 'cancelled' | 'pending';
  current_period_start: string;
  current_period_end: string;
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  fetchSubscription: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  isCurrentPlan: (planId: string) => boolean;
  isPaidPlan: () => boolean;
  getStatus: () => 'active' | 'inactive' | 'cancelled' | 'pending' | null;
  getCurrentPlanId: () => string;
  isUpgradePlan: (planId: string) => boolean;
  isDowngradePlan: (planId: string) => boolean;
  getPlanLevel: (planId: string) => number;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch subscription data from backend
   */
  const fetchSubscription = async () => {
    if (!isAuthenticated) {
      console.log('[SubscriptionContext] User not authenticated, skipping fetch');
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      console.log('[SubscriptionContext] Fetching subscription data...');
      setLoading(true);
      setError(null);

      const data = await apiService.getCurrentSubscription();
      console.log('[SubscriptionContext] Subscription data received:', data);
      setSubscription(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch subscription';
      console.error('[SubscriptionContext] Error fetching subscription:', {
        error: errorMessage,
        fullError: err,
        timestamp: new Date().toISOString()
      });
      setError(errorMessage);
      // Fallback to null (user will be treated as free tier)
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh subscription (call after payment verification)
   */
  const refreshSubscription = async () => {
    console.log('[SubscriptionContext] Refreshing subscription (called after payment)');
    await fetchSubscription();
  };

  /**
   * Check if user is on a specific plan
   */
  const isCurrentPlan = (planId: string): boolean => {
    if (!subscription) return planId === 'free';
    return subscription.plan_id === planId && subscription.status === 'active';
  };

  /**
   * Check if user has a paid (non-free) active plan
   */
  const isPaidPlan = (): boolean => {
    if (!subscription) return false;
    return subscription.plan_id !== 'free' && subscription.status === 'active';
  };

  /**
   * Get subscription status
   */
  const getStatus = (): 'active' | 'inactive' | 'cancelled' | 'pending' | null => {
    return subscription?.status || null;
  };

  /**
   * Get current plan ID
   */
  const getCurrentPlanId = (): string => {
    return subscription?.plan_id || 'free';
  };

  /**
   * Check if a plan is an upgrade from current plan
   * Plan hierarchy: free (0) < starter (1) < pro (2) < enterprise (3)
   */
  const isUpgradePlan = (planId: string): boolean => {
    const planHierarchy: Record<string, number> = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    const currentLevel = planHierarchy[getCurrentPlanId()] || 0;
    const selectedLevel = planHierarchy[planId] || 0;
    return selectedLevel > currentLevel;
  };

  /**
   * Check if a plan is a downgrade from current plan
   */
  const isDowngradePlan = (planId: string): boolean => {
    const planHierarchy: Record<string, number> = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    const currentLevel = planHierarchy[getCurrentPlanId()] || 0;
    const selectedLevel = planHierarchy[planId] || 0;
    return selectedLevel < currentLevel;
  };

  /**
   * Get plan hierarchy level (0 = free, 1 = starter, 2 = pro, 3 = enterprise)
   */
  const getPlanLevel = (planId: string): number => {
    const planHierarchy: Record<string, number> = { free: 0, starter: 1, pro: 2, enterprise: 3 };
    return planHierarchy[planId] || 0;
  };

  /**
   * Auto-fetch subscription on mount and when auth changes
   */
  useEffect(() => {
    console.log('[SubscriptionContext] useEffect triggered - isAuthenticated:', isAuthenticated);
    if (isAuthenticated) {
      console.log('[SubscriptionContext] User authenticated, fetching subscription');
      fetchSubscription();
    } else {
      console.log('[SubscriptionContext] User not authenticated, clearing subscription');
      setSubscription(null);
      setLoading(false);
    }
  }, [isAuthenticated]);

  const value: SubscriptionContextType = {
    subscription,
    loading,
    error,
    fetchSubscription,
    refreshSubscription,
    isCurrentPlan,
    isPaidPlan,
    getStatus,
    getCurrentPlanId,
    isUpgradePlan,
    isDowngradePlan,
    getPlanLevel,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * Hook to access subscription context
 * Must be used within a SubscriptionProvider
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
